import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { collection, addDoc, getDocs, Timestamp } from "firebase/firestore";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import AllCustomers from "./AllCustomers";
import { db } from "../../firebase";
import { setLoading } from "../../redux/loaderSlice";
import { validateCustomerForm } from "../../utils/validations";

const MAX_FILE_BYTES = 819200; // 800 KB

const Container = styled.div`
  padding: 16px;
  margin-top: 60px;
`;

const AddButton = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: 10px 18px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 20px;
  font-weight: 600;
  font-size: 14px;

  &:hover {
    opacity: 0.9;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: ${({ open }) => (open ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #ffffff;
  color: #111827;
  padding: 24px;
  width: 92%;
  max-width: 760px;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 13px;
    margin-bottom: 6px;
    font-weight: 600;
    color: #374151;
  }

  input {
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid ${({ hasError }) => (hasError ? "#DC2626" : "#D1D5DB")};
    font-size: 14px;
    background: #ffffff;
    color: #111827;
  }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 6px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 600;

  ${({ variant, theme }) =>
    variant === "primary"
      ? `
      background: ${theme.colors.accent};
      color: ${theme.colors.white};
    `
      : `
      background: #f0f0f0;
      color: ${theme.colors.text};
    `}
`;

const createEmptyForm = () => ({
  customerId: `CUS-${Date.now()}`,
  customerName: "",
  phone: "",
  email: "",
  address: "",
  profilePicture: "",
  profilePictureName: "",
  aadharCard: "",
  aadharCardName: "",
  panCard: "",
  panCardName: "",
  bankName: "",
  branch: "",
  accountNumber: "",
  ifsc: "",
});

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });

export default function Customers() {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState(createEmptyForm());
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const fetchCustomers = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const snapshot = await getDocs(collection(db, "customers"));
      const data = snapshot.docs.map((customerDoc) => ({
        id: customerDoc.id,
        ...customerDoc.data(),
      }));
      setCustomers(data);
    } catch (err) {
      toast.error("Failed to fetch customers");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const { errors: nextErrors } = validateCustomerForm(formData);
    const visibleErrors = Object.entries(nextErrors).reduce((acc, [key, value]) => {
      if (touchedFields[key]) acc[key] = value;
      return acc;
    }, {});
    setErrors(visibleErrors);
  }, [formData, touchedFields]);

  const validateField = (key) => {
    setTouchedFields((prev) => ({ ...prev, [key]: true }));
  };

  const handleChange = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "accountNumber") {
      value = value.replace(/\D/g, "");
    }
    if (name === "ifsc") {
      value = value.toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name);
  };

  const handleFileUpload = async (event, fieldName, nameField) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      toast.error("File too large. Max 800 KB allowed.");
      // clear the native file input so browser shows "No file chosen"
      try { event.target.value = ""; } catch (e) {}
      return;
    }

    try {
      const encoded = await toBase64(file);
      setFormData((prev) => ({
        ...prev,
        [fieldName]: encoded,
        [nameField]: file.name,
      }));
      validateField(fieldName);
    } catch (error) {
      toast.error("Failed to read file");
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setErrors({});
    setTouchedFields({});
    setFormData(createEmptyForm());
  };

  const saveCustomer = async () => {
    const validation = validateCustomerForm(formData);
    if (!validation.isValid) {
      const markAllTouched = Object.keys(validation.errors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouchedFields((prev) => ({ ...prev, ...markAllTouched }));
      toast.error("Please fix form errors");
      return;
    }

    dispatch(setLoading(true));
    try {
      const docRef = await addDoc(collection(db, "customers"), {
        ...formData,
        createdAt: Timestamp.now(),
      });
      // Firestore doc id is the source of truth for “create succeeded”
      toast.success(`Customer saved (id: ${docRef.id})`);
      closeModal();
      fetchCustomers();
    } catch (err) {
      console.error("saveCustomer:", err);
      toast.error(err?.message || "Failed to save customer");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  return (
    <Container>
      <h2 style={{ marginTop: 0, marginBottom: 16 }}>Your Customers</h2>

      <AllCustomers data={customers} refresh={fetchCustomers} />

      <ModalOverlay open={modalOpen}>
        <ModalContent>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Create Customer</h3>

          <FormGrid>
            <FormRow>
              <label>Customer ID</label>
              <input value={formData.customerId} readOnly />
            </FormRow>

            <FormRow hasError={errors.customerName}>
              <label>Customer Name</label>
              <input name="customerName" value={formData.customerName} onChange={handleChange} />
              {errors.customerName && <ErrorText>{errors.customerName}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.phone}>
              <label>Phone Number</label>
              <input name="phone" value={formData.phone} onChange={handleChange} />
              {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.email}>
              <label>Email</label>
              <input name="email" value={formData.email} onChange={handleChange} />
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.address}>
              <label>Address</label>
              <input name="address" value={formData.address} onChange={handleChange} />
              {errors.address && <ErrorText>{errors.address}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.bankName}>
              <label>Bank Name</label>
              <input name="bankName" value={formData.bankName} onChange={handleChange} />
              {errors.bankName && <ErrorText>{errors.bankName}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.branch}>
              <label>Branch</label>
              <input name="branch" value={formData.branch} onChange={handleChange} />
              {errors.branch && <ErrorText>{errors.branch}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.accountNumber}>
              <label>Account Number</label>
              <input name="accountNumber" value={formData.accountNumber} onChange={handleChange} />
              {errors.accountNumber && <ErrorText>{errors.accountNumber}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.ifsc}>
              <label>IFSC Code</label>
              <input name="ifsc" value={formData.ifsc} onChange={handleChange} />
              {errors.ifsc && <ErrorText>{errors.ifsc}</ErrorText>}
            </FormRow>

            {/* Profile Picture */}
            <FormRow hasError={errors.profilePicture}>
              <label>Profile Picture</label>
              <input name="profilePicture" type="file" accept="image/*" onChange={(event) => handleFileUpload(event, "profilePicture", "profilePictureName")} />
              {formData.profilePictureName && <small>{formData.profilePictureName}</small>}
              {errors.profilePicture && <ErrorText>{errors.profilePicture}</ErrorText>}
            </FormRow>

            {/* Aadhar Card */}
            <FormRow hasError={errors.aadharCard}>
              <label>Aadhar Card</label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(event) => handleFileUpload(event, "aadharCard", "aadharCardName")}
              />
              {formData.aadharCardName && <small>{formData.aadharCardName}</small>}
              {errors.aadharCard && <ErrorText>{errors.aadharCard}</ErrorText>}
            </FormRow>

            <FormRow hasError={errors.panCard}>
              <label>PAN Card</label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(event) => handleFileUpload(event, "panCard", "panCardName")}
              />
              {formData.panCardName && <small>{formData.panCardName}</small>}
              {errors.panCard && <ErrorText>{errors.panCard}</ErrorText>}
            </FormRow>
          </FormGrid>

          <ButtonRow>
            <Button onClick={closeModal}>Cancel</Button>
            <Button variant="primary" onClick={saveCustomer}>Save Customer</Button>
          </ButtonRow>
        </ModalContent>
      </ModalOverlay>
    </Container>
  );
}